import { CalendarPickerView, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { addCurrentTimeToDate } from '../utils/function'
import { AppTextField } from './AppTextField'
import { useFormFieldError } from './FormCore'
import { IconButton, InputAdornment } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { enGB, nb } from 'date-fns/locale'
import i18n from 'src/i18n'

// Follow the app language for the calendar (Norwegian by default), not the browser.
const getDateFnsLocale = () => (i18n.language?.toLowerCase().startsWith('en') ? enGB : nb)

export interface GenericDatePicker<T = any> {
  disabled?: boolean
  value?: T
  defaultValue?: T
  name: string
  label?: string
  error?: boolean
  inputFormat?: string
  helperText?: string
  field?: any
  views?: CalendarPickerView[]
  openTo?: CalendarPickerView
  shouldDisableDate?: ((day: Date) => boolean) | undefined
  shouldDisableYear?: ((year: any) => boolean) | undefined
  shouldDisableMonth?: ((month: any) => boolean) | undefined
  isWithoutAppForm?: boolean
  allowNullable?: boolean
  clearable?: boolean
}

interface DatePickerProps extends GenericDatePicker {
  onChange: (value: any) => any
  minDate?: Date | undefined
  maxDate?: Date | undefined
  onClick?: (value: any) => any
  onFocus?: (value: any) => any
}

interface CustomDatePickerProps extends GenericDatePicker {
  onChange?: (value: any) => any
  minDate?: Date | undefined
  maxDate?: Date | undefined
  onClick?: (value: any) => any
  onFocus?: (value: any) => any
  onViewChange?: (view: CalendarPickerView) => void
  style?: any
}

export default function BasicDatePicker({
  isWithoutAppForm = false,
  onChange,
  value,
  minDate,
  maxDate,
  onClick,
  onFocus,
  onViewChange,
  ...rest
}: CustomDatePickerProps) {
  if (isWithoutAppForm) {
    if (!onChange && value) {
      Logger.error('onChange & value required')
      return <></>
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
      {isWithoutAppForm ? (
        <GenericDatePicker
          onChange={onChange as (value: any) => any}
          value={value}
          minDate={minDate}
          maxDate={maxDate}
          {...rest}
          onClick={onClick}
          onFocus={onFocus}
          onViewChange={onViewChange}
        />
      ) : (
        <DatePickerWithAppForm
          onChange={onChange}
          value={value}
          {...rest}
          minDate={minDate}
          maxDate={maxDate}
          onClick={onClick}
          onFocus={onFocus}
        />
      )}
    </LocalizationProvider>
  )
}

const DatePickerWithAppForm: React.FC<CustomDatePickerProps> = ({
  helperText,
  error,
  name,
  value,
  onChange,
  minDate,
  maxDate,
  allowNullable,
  onClick,
  onFocus,
  clearable = false,
  ...rest
}) => {
  const { hasError, message } = useFormFieldError(name)
  const { field } = useController({ name })

  const { setError, clearErrors } = useFormContext()
  const { t } = useTranslation()

  const handleClear = () => {
    onChange ? onChange(null) : field.onChange(null)
    clearErrors(name)
  }

  return (
    <GenericDatePicker
      name={name}
      error={error ?? hasError}
      helperText={helperText ?? message}
      value={value ?? field.value}
      onChange={date => {
        onChange ? onChange(addCurrentTimeToDate(date)) : field.onChange(addCurrentTimeToDate(date))
        if (date || allowNullable) {
          clearErrors(name)
        } else {
          setError(name, { type: 'required', message: t('required') })
        }
      }}
      minDate={minDate}
      maxDate={maxDate}
      clearable={clearable}
      onClear={handleClear}
      {...rest}
      onClick={onClick}
      onFocus={onFocus}
    />
  )
}

const GenericDatePicker: React.FC<
  DatePickerProps & { onClear?: () => void; onViewChange?: (view: CalendarPickerView) => void }
> = ({
  value,
  defaultValue,
  name,
  error,
  helperText,
  inputFormat,
  minDate,
  maxDate,
  onClick,
  onFocus,
  clearable = false,
  onClear,
  disabled,
  onViewChange,
  ...rest
}) => {
  return (
    <DatePicker
      value={value || defaultValue || null}
      inputFormat={inputFormat ?? 'yyyy-MM-dd'}
      mask={inputFormat ? undefined : '____-__-__'}
      minDate={minDate ? new Date(minDate as Date) : undefined}
      maxDate={maxDate ? new Date(maxDate as Date) : undefined}
      disabled={disabled}
      onViewChange={onViewChange}
      {...rest}
      renderInput={params => (
        <AppTextField
          type="date"
          variant="outlined"
          value={addCurrentTimeToDate(value || defaultValue) || null}
          fullWidth
          helperText={helperText}
          name={name}
          {...params}
          defaultValue={defaultValue}
          error={error}
          onClick={onClick}
          onFocus={onFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {clearable && value && !disabled && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={e => {
                        e.stopPropagation()
                        onClear?.()
                      }}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )}
                {params.InputProps?.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export interface GenericDateTimePicker<T = any> {
  value?: T
  label?: string
  onChange: (value: T) => void
  name: string
  disable?: boolean
  fullWidth?: boolean
}

export interface DateTimePickerProps extends GenericDateTimePicker {
  isWithoutAppForm?: boolean
}

export const DateTimePickerGenericComponent: React.FC<DateTimePickerProps> = ({ isWithoutAppForm, ...props }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
      {isWithoutAppForm ? <DateTimePickerWithoutAppFormComponent {...props} /> : <DateTimePickerComponent {...props} />}
    </LocalizationProvider>
  )
}

export const DateTimePickerWithoutAppFormComponent: React.FC<GenericDateTimePicker> = ({
  value,
  onChange,
  label,
  disable,
  fullWidth,
}) => {
  return (
    <DateTimePicker
      label={label}
      value={value}
      onChange={onChange}
      renderInput={params => <AppTextField {...params} fullWidth={fullWidth} />}
      closeOnSelect
      inputFormat={'yyyy-MM-dd hh:mm a'}
      disabled={disable}
    />
  )
}

export const DateTimePickerComponent: React.FC<GenericDateTimePicker> = ({
  value,
  onChange,
  label,
  name,
  disable,
  fullWidth,
}) => {
  const { field } = useController({ name })

  return (
    <DateTimePicker
      label={label}
      value={value ?? field.value}
      onChange={onChange}
      renderInput={params => <AppTextField {...params} fullWidth={fullWidth} />}
      closeOnSelect
      inputFormat={'yyyy-MM-dd hh:mm a'}
      disabled={disable}
    />
  )
}
